import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { useFilterPresetsStore } from '../../store/filterPresetsStore';
import { queryUsesResponseBodyFields } from '../../utils/tql/tql-to-sql';
import {
  createTrackedHarFetchErrorMatcher,
  createTrackedHarResponseBodyMatcher,
} from './evaluateTrackedHarResponseBodyMatches';
import type { HARRequest } from './mapHarEntryToHarRequest';
import {
  createTrackedHarFetchRegistry,
  fetchTrackedHarByIdWithoutCaching,
} from './useTrackedHarById';

const BODY_SEARCH_DEBOUNCE_MS = 300;
const FETCH_CONCURRENCY = 5;

const mapWithConcurrency = async <TItem, TResult>({
  items,
  concurrency,
  mapper,
  signal,
}: {
  items: TItem[];
  concurrency: number;
  mapper: (item: TItem) => Promise<TResult>;
  signal: AbortSignal;
}): Promise<TResult[]> => {
  if (items.length === 0) {
    return [];
  }

  const results = new Array<TResult>(items.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        if (signal.aborted) {
          return;
        }
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await mapper(items[currentIndex]);
      }
    },
  );

  await Promise.all(workers);
  return results;
};

export type ResponseBodySearchRowOutcome =
  | { kind: 'match'; id: string }
  | { kind: 'miss' }
  | { kind: 'error'; id: string }
  | { kind: 'aborted' };

export type TrackedHarResponseBodySearchResult = {
  isActive: boolean;
  isSearching: boolean;
  matchSet: Set<string> | null;
  fetchErrorSet: Set<string>;
};

export const collectResponseBodySearchMatchIds = (
  outcomes: ResponseBodySearchRowOutcome[],
): {
  matchIds: Set<string>;
  fetchErrorIds: Set<string>;
  hasFetchError: boolean;
} => {
  const matchIds = new Set<string>();
  const fetchErrorIds = new Set<string>();
  let hasFetchError = false;

  for (const outcome of outcomes) {
    if (outcome.kind === 'error') {
      hasFetchError = true;
      fetchErrorIds.add(outcome.id);
      continue;
    }
    if (outcome.kind === 'match') {
      matchIds.add(outcome.id);
    }
  }

  return { matchIds, fetchErrorIds, hasFetchError };
};

const isAbortError = ({
  error,
  signal,
}: {
  error: unknown;
  signal: AbortSignal;
}): boolean => {
  if (signal.aborted) {
    return true;
  }
  return error instanceof Error && error.name === 'AbortError';
};

export const filterRequestsByResponseBodySearch = ({
  requests,
  search,
}: {
  requests: HARRequest[];
  search: TrackedHarResponseBodySearchResult;
}): HARRequest[] => {
  if (!search.isActive) {
    return requests;
  }

  const { matchSet } = search;
  if (!matchSet) {
    return [];
  }

  return requests.filter(
    (request) =>
      matchSet.has(request.id) || search.fetchErrorSet.has(request.id),
  );
};

export const useTrackedHarResponseBodySearch = ({
  applicationId,
  requests,
  tqlQuery,
  filterMode,
}: {
  applicationId?: string;
  requests: HARRequest[] | null;
  tqlQuery: string;
  filterMode: 'simple' | 'advanced';
}): TrackedHarResponseBodySearchResult => {
  const resolvePreset = useFilterPresetsStore((state) => state.resolvePreset);
  const queryClient = useQueryClient();
  const tFullHar = useTranslations('httpHistory.fullHar');
  const tFullHarRef = useRef(tFullHar);
  tFullHarRef.current = tFullHar;
  const [matchSet, setMatchSet] = useState<Set<string> | null>(null);
  const [fetchErrorSet, setFetchErrorSet] = useState<Set<string>>(new Set());
  const searchVersionRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const fetchRegistryRef = useRef(createTrackedHarFetchRegistry());

  const isActive = useMemo(() => {
    if (filterMode !== 'advanced' || !tqlQuery.trim()) {
      return false;
    }
    return queryUsesResponseBodyFields(tqlQuery, resolvePreset);
  }, [filterMode, tqlQuery, resolvePreset]);

  const requestIdsKey = useMemo(
    () => (requests ?? []).map((request) => request.id).join(','),
    [requests],
  );
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const hasLoadedRequests = requests !== null;
  const isSearching = isActive && matchSet === null;

  useEffect(() => {
    const currentRequests = requestsRef.current;
    if (!isActive || !applicationId || !currentRequests) {
      setMatchSet(null);
      setFetchErrorSet(new Set());
      return;
    }

    setMatchSet(null);
    setFetchErrorSet(new Set());

    const abortController = new AbortController();

    const executeSearch = () => {
      const version = ++searchVersionRef.current;
      if (
        abortController.signal.aborted ||
        version !== searchVersionRef.current
      ) {
        return;
      }

      const requestsToSearch = requestsRef.current;
      if (!requestsToSearch || requestsToSearch.length === 0) {
        setMatchSet(new Set());
        return;
      }
      const matchesBodyQuery = createTrackedHarResponseBodyMatcher({
        tqlQuery,
        resolvePreset,
      });
      const matchesFetchErrorFallback = createTrackedHarFetchErrorMatcher({
        tqlQuery,
        resolvePreset,
      });

      const notifyBodySearchFetchError = () => {
        toasterApi.error({
          title: tFullHarRef.current('detailsLoadErrorTitle'),
          description: tFullHarRef.current('addToChatErrorDescription'),
        });
      };

      void mapWithConcurrency({
        items: requestsToSearch,
        concurrency: FETCH_CONCURRENCY,
        signal: abortController.signal,
        mapper: async (request): Promise<ResponseBodySearchRowOutcome> => {
          if (abortController.signal.aborted) {
            return { kind: 'aborted' };
          }

          try {
            const isBodyMatch = matchesBodyQuery(
              await fetchTrackedHarByIdWithoutCaching({
                queryClient,
                registry: fetchRegistryRef.current,
                applicationId,
                harEntryId: request.id,
              }),
            );
            if (abortController.signal.aborted) {
              return { kind: 'aborted' };
            }
            return isBodyMatch
              ? { kind: 'match', id: request.id }
              : { kind: 'miss' };
          } catch (error: unknown) {
            if (isAbortError({ error, signal: abortController.signal })) {
              return { kind: 'aborted' };
            }
            return { kind: 'error', id: request.id };
          }
        },
      })
        .then((outcomes) => {
          if (
            abortController.signal.aborted ||
            version !== searchVersionRef.current
          ) {
            return;
          }

          const { matchIds, fetchErrorIds, hasFetchError } =
            collectResponseBodySearchMatchIds(outcomes);
          const visibleFetchErrorIds = new Set(
            requestsToSearch
              .filter(
                (request) =>
                  fetchErrorIds.has(request.id) &&
                  matchesFetchErrorFallback(request),
              )
              .map((request) => request.id),
          );
          setMatchSet(matchIds);
          setFetchErrorSet(visibleFetchErrorIds);
          if (hasFetchError) {
            notifyBodySearchFetchError();
          }
        })
        .catch((error: unknown) => {
          if (
            abortController.signal.aborted ||
            version !== searchVersionRef.current
          ) {
            return;
          }
          setMatchSet(new Set());
          if (!isAbortError({ error, signal: abortController.signal })) {
            setFetchErrorSet(
              new Set(
                requestsToSearch
                  .filter(matchesFetchErrorFallback)
                  .map((request) => request.id),
              ),
            );
            notifyBodySearchFetchError();
          }
        });
    };

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(executeSearch, BODY_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceRef.current);
      abortController.abort();
      searchVersionRef.current += 1;
    };
  }, [
    isActive,
    applicationId,
    requestIdsKey,
    hasLoadedRequests,
    tqlQuery,
    resolvePreset,
    queryClient,
  ]);

  return {
    isActive,
    isSearching,
    matchSet,
    fetchErrorSet,
  };
};
